var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { checkUserTypeOptional } from "healthex-common/checkusertype";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
import { UserType } from "healthex-common/user.dtos";
import { computed, observable } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { SmartAuthErrorReason, SmartAuthProvider, SmartAuthState, SmartAuthType } from "../Logic/SmartAuthManager";
import { NamedRoutes } from "./NamedRoutes";
import { getTranslatedStudyText } from "./TranslationService";
const t = getObservableT("patients");
export class PatientLandingViewModel {
    constructor(container) {
        this.clearAuthProvider = SmartAuthProvider.CLEAR_IAL2;
        this.isSandboxLogin = false;
        this.loginUIManager = container.loginUIManager;
        this.smartAuthManager = container.smartAuthManager;
        this.smartAuthWindow = container.smartAuthWindow;
        this.routingUIManager = container.routingUIManager;
        this.patientConsentUIManager = container.patientConsentUIManager;
        this.authManager = container.authManager;
        this.persistence = container.persistence;
        this.isSandboxLogin = this.persistence.isSandboxLogin();
    }
    get studyInfo() {
        if (!this.patientConsentUIManager.isRelevantRoute && !this.patientConsentUIManager.isInteractivePreview) {
            return null;
        }
        return this.patientConsentUIManager.studyInfo;
    }
    get organization() {
        if (this.patientConsentUIManager.isRelevantRoute || this.patientConsentUIManager.isInteractivePreview) {
            if (this.patientConsentUIManager.studyInfo === NOT_LOADED)
                return NOT_LOADED;
            if (this.patientConsentUIManager.studyInfo) {
                return this.patientConsentUIManager.studyInfo.addedByOrganization;
            }
        }
        if (this.loginUIManager.organization === NOT_LOADED)
            return NOT_LOADED;
        return checkUserTypeOptional(this.loginUIManager.organization, UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER);
    }
    get organizationIsLive() {
        if (this.organization === NOT_LOADED)
            return NOT_LOADED;
        if (!this.organization) {
            return false;
        }
        else {
            return this.organization.isLive;
        }
    }
    get pageTitle() {
        if (this.studyInfo) {
            return this.getCustomizedText("loginTitle");
        }
        else {
            return t("Welcome to HealthEx");
        }
    }
    get supportingText() {
        if (this.smartAuthErrorReason) {
            return this.smartAuthErrorReason === SmartAuthErrorReason.PATIENT_NOT_FOUND
                ? this.smartAuthError
                : t("An error occurred while logging you in: {{ smartAuthError }}", {
                    smartAuthError: this.smartAuthError,
                });
        }
        return t("Log in or sign up for HealthEx using CLEAR to verify your identity and keep your information secure.");
    }
    get clearButtonLabel() {
        if (this.smartAuthErrorReason) {
            return t("Try again");
        }
        return t("Continue with CLEAR");
    }
    get flipLongPressLoginButton() {
        return this.isSandboxLogin;
    }
    get smartAuthState() {
        return this.smartAuthManager.state;
    }
    get smartAuthError() {
        var _a;
        return (_a = this.smartAuthManager.error) !== null && _a !== void 0 ? _a : "";
    }
    get smartAuthErrorReason() {
        return this.smartAuthManager.errorReason;
    }
    get isClearDefault() {
        if (this.loginUIManager.previousAuthenticationProvider === GFIDProvider.SMART_ON_FHIR && this.showProviderPortal) {
            return false;
        }
        return this.showClear;
    }
    get showClear() {
        var _a;
        if (!this.organization || this.organization === NOT_LOADED)
            return false;
        if (this.organization.type === UserType.EXTERNAL_RESEARCHER) {
            return true;
        }
        else {
            return (this.organization.patientAuthenticationProviders.includes(GFIDProvider.CLEAR) && !((_a = this.organization) === null || _a === void 0 ? void 0 : _a.forcePatientPortalEmbed));
        }
    }
    get showProviderPortal() {
        var _a;
        if (!this.organization || this.organization === NOT_LOADED)
            return false;
        if (this.organization.type === UserType.EXTERNAL_RESEARCHER) {
            return false;
        }
        else {
            return (this.organization.patientAuthenticationProviders.includes(GFIDProvider.SMART_ON_FHIR) &&
                !((_a = this.organization) === null || _a === void 0 ? void 0 : _a.forcePatientPortalEmbed));
        }
    }
    get showAnotherHealthcareProvider() {
        if (!this.organization || this.organization === NOT_LOADED)
            return false;
        return this.organization.type === UserType.HEALTHCARE_ORGANIZATION && this.studyInfo === null;
    }
    get isPreview() {
        if (this.patientConsentUIManager.isInteractivePreview) {
            return true;
        }
        return false;
    }
    get isLoggingIn() {
        return this.smartAuthManager.state === SmartAuthState.IN_PROGRESS;
    }
    getCustomizedText(key) {
        if (this.studyInfo !== NOT_LOADED && this.studyInfo) {
            return getTranslatedStudyText(this.studyInfo, key, this.patientConsentUIManager.currentLanguage) || "";
        }
        return "";
    }
    async continueWithClear(sandboxMode) {
        if (this.patientConsentUIManager.isInteractivePreview) {
            this.patientConsentUIManager.goToNextPage();
            return;
        }
        this.persistence.setSandboxLogin(sandboxMode);
        await this.startSmartAuth(this.clearAuthProvider, !!this.smartAuthErrorReason, sandboxMode ? true : undefined);
    }
    async continueWithPatientPortal() {
        if (this.patientConsentUIManager.isInteractivePreview) {
            this.patientConsentUIManager.goToNextPage();
            return;
        }
        await this.startSmartAuth(SmartAuthProvider.EPIC);
    }
    async tryAgain() {
        if (!this.currentSmartAuthProvider) {
            console.warn("No saved auth provider");
            return;
        }
        await this.startSmartAuth(this.currentSmartAuthProvider, true);
    }
    async startSmartAuth(provider, isTryingAgain, sandboxMode) {
        if (!this.organization || this.organization === NOT_LOADED)
            return;
        this.currentSmartAuthProvider = provider;
        if (provider === SmartAuthProvider.CLEAR_IAL2) {
            await this.smartAuthManager.redirectToClearForOAuth(SmartAuthType.PATIENT_AUTH, window.location.hash, this.organization.id, isTryingAgain, sandboxMode);
        }
        else {
            await this.smartAuthWindow.createSmartAuthWindow(provider, SmartAuthType.PATIENT_AUTH, this.organization.id, sandboxMode);
        }
    }
    handleClearIAL2LoginRedirectIfNeeded() {
        var _a;
        const callbackHash = this.smartAuthManager.getClearIAL2CallbackHash();
        const isValidRoute = ((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.LOGIN_AS_PATIENT.routeName;
        if (callbackHash && isValidRoute) {
            if (!this.authManager.isLoggedIn) {
                this.currentSmartAuthProvider = SmartAuthProvider.CLEAR_IAL2;
            }
            else {
            }
            this.smartAuthManager.setClearIAL2CallbackHash(null);
        }
    }
    async continueWithOpenPortal() {
        if (!this.organization || this.organization === NOT_LOADED)
            return;
        this.routingUIManager.navigateTo(NamedRoutes.LOGIN_REDIRECT_TO_EMBED, { organizationId: this.organization.id });
    }
    loginAsOrganizationAdmin() {
        this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_ORG_USER);
    }
    goBackToLoginMethodSelection() {
        this.currentSmartAuthProvider = undefined;
        this.smartAuthManager.resetAuth();
    }
    get isScreenshotMode() {
        return this.patientConsentUIManager.isScreenshotMode;
    }
}
__decorate([
    observable,
    __metadata("design:type", Boolean)
], PatientLandingViewModel.prototype, "isSandboxLogin", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "studyInfo", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "organization", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "organizationIsLive", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "pageTitle", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "supportingText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "clearButtonLabel", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "flipLongPressLoginButton", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "smartAuthState", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "smartAuthError", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "smartAuthErrorReason", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "isClearDefault", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "showClear", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "showProviderPortal", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "showAnotherHealthcareProvider", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "isPreview", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "isLoggingIn", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], PatientLandingViewModel.prototype, "isScreenshotMode", null);
